import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import EmployeeCornerSidebar from "./EmployeeCornerSidebar";

const normalizeEmployeeId = (value) => String(value || "").trim().toUpperCase();
const isExEmployeeId = (value) => normalizeEmployeeId(value).startsWith("EX-");
const dayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const formatMonthForApi = (yearMonth) => {
  const [year, month] = String(yearMonth || "").split("-");
  if (!year || !month) return "";
  const mon = new Date(Number(year), Number(month) - 1).toLocaleString("en-US", {
    month: "short",
  });
  return `${mon}-${year}`;
};

// Helper to format date for display
const formatDateDisplay = (dateStr) => {
  if (!dateStr) return "-";
  if (typeof dateStr !== "string") {
    const dObj = new Date(dateStr);
    if (Number.isNaN(dObj.getTime())) return "-";
    const y = dObj.getFullYear();
    const m = String(dObj.getMonth() + 1).padStart(2, "0");
    const d = String(dObj.getDate()).padStart(2, "0");
    return `${d}-${m}-${y}`;
  }
  const [y, m, d] = dateStr.split("-");
  if (!y || !m || !d) return dateStr;
  return `${d}-${m}-${y}`; // Returns DD-MM-YYYY
};
const formatDayDisplay = (dateStr) => {
  if (!dateStr) return "";
  const dObj =
    typeof dateStr === "string"
      ? new Date(`${dateStr}T00:00:00`)
      : new Date(dateStr);
  if (Number.isNaN(dObj.getTime())) return "";
  return dayLabels[dObj.getDay()] || "";
};
const formatOTDisplay = (otValue) => {
  const val = parseFloat(otValue);
  if (!val || val <= 0) return "--";
  const totalMinutes = Math.round(val * 60);
  if (totalMinutes < 240) return "--";
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${h}h ${m}m`;
};

const parseTimeToMinutes = (timeStr) => {
  if (!timeStr || typeof timeStr !== "string" || timeStr === "--") return null;
  const normalized = timeStr.replace(/\./g, ":").trim();
  const parts = normalized.split(" ");
  if (parts.length < 2) return null;
  const clock = parts[0];
  const meridiem = parts[1].toUpperCase();
  const hm = clock.split(":").map(Number);
  if (hm.length < 2 || Number.isNaN(hm[0]) || Number.isNaN(hm[1])) return null;
  let [hours, minutes] = hm;
  if (meridiem === "PM" && hours < 12) hours += 12;
  if (meridiem === "AM" && hours === 12) hours = 0;
  return hours * 60 + minutes;
};

const getAttendanceActionState = ({ todaysRecord, yesterdaysRecord }) => {
  const normalizeStatus = (value) => String(value || "").trim().toUpperCase();
  const hasPunchIn = (record) => {
    const value = String(record?.checkInTime || "").trim();
    return Boolean(value && value !== "--");
  };
  const hasPunchOut = (record) => {
    const value = String(record?.checkOutTime || "").trim();
    return Boolean(value && value !== "--");
  };
  const isLockedStatus = (status) =>
    status === "ABSENT" ||
    status === "OFF" ||
    status.startsWith("SL") ||
    status.startsWith("CL");

  const yesterdayStatus = normalizeStatus(yesterdaysRecord?.status);
  const yesterdayOpenNightShift =
    hasPunchIn(yesterdaysRecord) &&
    !hasPunchOut(yesterdaysRecord) &&
    !isLockedStatus(yesterdayStatus);

  if (yesterdayOpenNightShift) {
    return {
      label: "OUT",
      disabled: false,
      tone: "out",
      openRecord: yesterdaysRecord,
      isNightShiftPending: true,
    };
  }

  const todayStatus = normalizeStatus(todaysRecord?.status);
  const todayHasIn = hasPunchIn(todaysRecord);
  const todayHasOut = hasPunchOut(todaysRecord);

  if (isLockedStatus(todayStatus) || (todayHasIn && todayHasOut)) {
    return {
      label: "MARKED",
      disabled: true,
      tone: "done",
      openRecord: null,
      isNightShiftPending: false,
    };
  }

  if (todayHasIn) {
    return {
      label: "OUT",
      disabled: false,
      tone: "out",
      openRecord: todaysRecord,
      isNightShiftPending: false,
    };
  }

  return {
    label: "IN",
    disabled: false,
    tone: "in",
    openRecord: null,
    isNightShiftPending: false,
  };
};

const EmployeeAttendance = () => {
  const [history, setHistory] = useState([]);
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const attendanceRequestInFlightRef = useRef(false);
  const [employeeFullName, setEmployeeFullName] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM
  const [selectedMonthShifts, setSelectedMonthShifts] = useState({});
  
  const getStoredEmployeeUser = () => {
    try {
      const raw = localStorage.getItem("employeeUser");
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (err) {
      return null;
    }
  };

  const loggedUser = getStoredEmployeeUser();

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`/api/attendance/my/${loggedUser.employeeUserId}`);
      setHistory(res.data);
    } catch (err) {
      toast.error(err);
    }
  };

  useEffect(() => {
    const getMasterData = async () => {
      try {
        const res = await axios.get("/api/employees");
        if (res.data && Array.isArray(res.data)) {
          const targetEmployee = res.data.find(emp => emp.employeeUserId === loggedUser.employeeUserId);
          if (targetEmployee) {
            const fName = targetEmployee.firstName || "";
            const mName = targetEmployee.middleName || "";
            const lName = targetEmployee.lastName || "";
            const fullName = `${fName} ${mName} ${lName}`.replace(/\s+/g, ' ').trim();
            setEmployeeFullName(fullName);
          }
        }
      } catch (err) {
        toast.error("Could not access the employee list API");
      }
    };

    if (loggedUser?.employeeUserId) {
      getMasterData();
      fetchHistory();
    }
  }, [loggedUser?.employeeUserId]);

  useEffect(() => {
    const fetchShiftData = async () => {
      try {
        if (!loggedUser?.employeeUserId) {
          setSelectedMonthShifts({});
          return;
        }
        const monthParam = formatMonthForApi(selectedMonth);
        if (!monthParam) {
          setSelectedMonthShifts({});
          return;
        }

        const res = await axios.get(`/api/shift-management/${monthParam}`);
        const list = Array.isArray(res.data) ? res.data : [];
        const record = list.find((item) => item.employeeUserId === loggedUser.employeeUserId);
        setSelectedMonthShifts(record?.shifts || {});
      } catch (err) {
        setSelectedMonthShifts({});
      }
    };

    fetchShiftData();
  }, [loggedUser?.employeeUserId, selectedMonth]);

// --- NEW LOGIC FOR NIGHT SHIFT ---
  const todayStr = new Date().toISOString().split('T')[0];
  
  // Get Yesterday's date string
  const yesterdayDate = new Date();
  yesterdayDate.setDate(yesterdayDate.getDate() - 1);
  const yesterdayStr = yesterdayDate.toISOString().split('T')[0];
  
  const getMonthKey = (doc) => {
    if (!doc) return "";
    const y = Number(doc.year);
    const m = Number(doc.month);
    if (!y || !m) return "";
    return `${y}-${String(m).padStart(2, "0")}`;
  };

  const currentMonthKey = todayStr.slice(0, 7);
  const currentDoc = history.find((h) => getMonthKey(h) === currentMonthKey);

  const allRecords =
    currentDoc && Array.isArray(currentDoc.records)
      ? currentDoc.records
      : [];
  
  // Find records for both days
  const todaysRecord = allRecords.find(rec => rec.date === todayStr);
  const yesterdaysRecord = allRecords.find(rec => rec.date === yesterdayStr);
  const attendanceActionState = getAttendanceActionState({
    todaysRecord,
    yesterdaysRecord,
  });
  const { label: actionLabel, disabled: actionDisabled, tone: actionTone, openRecord, isNightShiftPending } =
    attendanceActionState;

  const getLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject("Geolocation not supported");
      }

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          resolve({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
          });
        },
        () => reject("Location permission denied"),
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 0,
        }
      );
    });
  };

  const handleAttendanceAction = async () => {
    if (attendanceRequestInFlightRef.current || loading) return;

    if (!loggedUser || !loggedUser.employeeUserId) {
      toast.error("You must be logged in!");
      return;
    }

    if (actionDisabled) return;

    const isPunchOutAction = actionLabel === "OUT";

    if (isPunchOutAction && openRecord?.shiftStartTime && openRecord?.shiftEndTime) {
      const startMin = parseTimeToMinutes(openRecord.shiftStartTime);
      const endMinRaw = parseTimeToMinutes(openRecord.shiftEndTime);
      if (startMin != null && endMinRaw != null) {
        const now = new Date();
        const shiftDate = new Date(openRecord.date);
        const endDateTime = new Date(shiftDate);

        let endMin = endMinRaw;
        if (endMin < startMin) {
          endDateTime.setDate(endDateTime.getDate() + 1);
          endMin += 1440;
        }

        endDateTime.setHours(Math.floor(endMin / 60), endMin % 60, 0, 0);

        if (now < endDateTime) {
          const proceed = window.confirm("Your shift is not ending. Do you punch out?");
          if (!proceed) return;
        }
      }
    }

  attendanceRequestInFlightRef.current = true;
  setLoading(true);
  try {
    const resolvedEmployeeId = loggedUser.employeeID || loggedUser.employeeId || "";
    if (isExEmployeeId(resolvedEmployeeId)) {
      toast.error("Ex-employee attendance is blocked.");
      return;
    }
    const storageName = `${loggedUser.firstName || ""} ${loggedUser.lastName || ""}`.trim();
    const nameToStore = employeeFullName || storageName || loggedUser.employeeUserId;
    const loc = await getLocation();
    const res = await axios.post("/api/attendance/mark", {
      employeeId: resolvedEmployeeId,
      employeeUserId: loggedUser.employeeUserId,
      employeeName: nameToStore,
      latitude: loc.latitude,
      longitude: loc.longitude,
      accuracy: loc.accuracy,
    });
    
    // Check if the backend returned a successful save but with an "Absent" status
    if (res.data.status === "Absent") {
      toast.error(res.data.message, { duration: 5000 }); // Red toast for late entry
    } else {
      toast.success(res.data.message); // Green toast for "Present" or "Out"
    }

    fetchHistory();
  } catch (err) {
    // This catches the 400 error (Too Early / No Shift) and shows the backend message
    const errorMsg = err.response?.data?.message || "Error marking attendance";
    toast.error(errorMsg);
  } finally {
    attendanceRequestInFlightRef.current = false;
    setLoading(false);
  }
};

  const selectedDoc = history.find((h) => getMonthKey(h) === selectedMonth);
  const hasAssignedShiftInSelectedMonth = useMemo(
    () =>
      Object.values(selectedMonthShifts || {}).some((value) =>
        Boolean(String(value || "").trim())
      ),
    [selectedMonthShifts]
  );
  const hasAttendanceInSelectedMonth = useMemo(
    () =>
      Boolean(
        selectedDoc &&
          Array.isArray(selectedDoc.records) &&
          selectedDoc.records.some((rec) => String(rec?.date || "").startsWith(selectedMonth))
      ),
    [selectedDoc, selectedMonth]
  );
  const filteredRecords = useMemo(() => {
    const [year, month] = selectedMonth.split("-").map(Number);
    if (!year || !month) return [];
    if (!hasAssignedShiftInSelectedMonth && !hasAttendanceInSelectedMonth) return [];

    const today = new Date();
    const isCurrentMonth =
      year === today.getFullYear() && month === today.getMonth() + 1;
    const totalDaysInMonth = new Date(year, month, 0).getDate();
    const totalDays = isCurrentMonth ? Math.min(today.getDate(), totalDaysInMonth) : totalDaysInMonth;
    const recordMap = new Map(
      (selectedDoc?.records || [])
        .filter((rec) => String(rec?.date || "").startsWith(selectedMonth))
        .map((rec) => [String(rec.date), rec])
    );

    const rows = [];
    for (let day = 1; day <= totalDays; day += 1) {
      const dateKey = `${selectedMonth}-${String(day).padStart(2, "0")}`;
      const existing = recordMap.get(dateKey);
      if (existing) {
        rows.push(existing);
        continue;
      }

      const rawShiftCode = selectedMonthShifts?.[day] || selectedMonthShifts?.[String(day)] || "";
      const normalizedShift = String(rawShiftCode || "").trim().toUpperCase();
      const isAssigned = Boolean(normalizedShift);

      rows.push({
        date: dateKey,
        shiftCode: isAssigned ? normalizedShift : "",
        checkInTime: "--",
        shiftStartTime: "--",
        shiftEndTime: "--",
        checkOutTime: "--",
        workDuration: "--",
        actualWorkDuration: "--",
        isOT: false,
        otHours: 0,
        status: "",
        isLate: false,
      });
    }

    return rows.sort((a, b) => String(b?.date || "").localeCompare(String(a?.date || "")));
  }, [selectedDoc, selectedMonth, selectedMonthShifts, hasAssignedShiftInSelectedMonth, hasAttendanceInSelectedMonth]);

  if (!loggedUser) {
    return (
      <div className="flex flex-col md:flex-row min-h-screen bg-gray-50">
        <EmployeeCornerSidebar />
        <div className="flex-1 p-4 sm:p-6">
          <div className="bg-white rounded-xl shadow-md p-6 text-center text-gray-600 font-semibold">
            Employee session not found. Please login again.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-gray-50">
      <EmployeeCornerSidebar />
      <div className="flex-1 p-4 sm:p-6">
        
      {/* Top Header Section */}
      <div className="bg-white rounded-xl shadow-md p-4 mb-6 border-t-4 border-dorika-blue">
        {/* Changed flex-col to a grid on mobile, and flex-row on larger screens */}
        <div className="grid grid-cols-2 md:flex md:flex-row items-center justify-between gap-4">
          
          {/* 1. Title - Stays left */}
          <h2 className="text-lg md:text-xl font-bold text-gray-800 whitespace-nowrap order-1">
            Daily Attendance
          </h2>

          {/* 2. Today's Date - Moves to top right on mobile */}
          <div className="text-blue-600 font-semibold whitespace-nowrap text-sm md:text-base text-right order-2 md:order-4">
            {formatDateDisplay(todayStr)}
          </div>

          {/* 3. Filter - Bottom left on mobile */}
          <div className="flex items-center gap-2 order-3 md:order-2">
            <span className="text-xs md:text-sm font-medium text-gray-500">Filter:</span>
            <input 
              type="month" 
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="border rounded-md p-1 text-xs md:text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 w-full max-w-[120px]"
            />
          </div>

          {/* 4. Action Button - Bottom right on mobile */}
          <div className="flex justify-end order-4 md:order-3">
            <button
              onClick={handleAttendanceAction}
              disabled={loading || actionDisabled}
              className={`${
                actionTone === "out"
                  ? "bg-orange-600 hover:bg-orange-700"
                  : actionTone === "done"
                  ? "bg-gray-500"
                  : "bg-green-600 hover:bg-green-700"
              } text-white px-4 md:px-8 py-1 rounded-lg font-bold transition-all shadow-md disabled:bg-gray-400 whitespace-nowrap text-sm md:text-base w-full md:w-auto`}
            >
              {loading ? "..." : actionLabel}
            </button>
          </div>
        </div>
      </div>

        {/* History Table */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="bg-dorika-blue p-3 text-white font-bold text-center">
            Attendance History ({selectedMonth})
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-center">
        <thead className="bg-gray-100 text-gray-700">
          <tr>
            <th className="p-3 border">Date</th>
            <th className="p-3 border">Shift</th>
            <th className="p-3 border text-green-600">Punch In</th> {/* New */}
            <th className="p-3 border">Shift Start</th> {/* New */}
            <th className="p-3 border">Shift End</th> {/* New */}
            <th className="p-3 border text-orange-600">Punch Out</th> {/* New */}
            <th className="p-3 border text-blue-600">Shift Work Time</th> {/* Official */}
            <th className="p-3 border text-purple-600">Actual Work Time</th>
            <th className="p-3 border text-red-600">OT Hours</th>
            <th className="p-3 border">Status</th>
          </tr>
        </thead>
          <tbody>
                {filteredRecords.length > 0 ? (
                  filteredRecords.map((rec, index) => {
                   

                    const statusText = String(rec?.status || "");
                    return (
                      <tr 
                        key={index} 
                        className={`border-b ${statusText.includes('Holiday') ? 'bg-yellow-50' : 'hover:bg-blue-50'}`}
                      >
                        <td className="p-3 border font-medium">
                          <div className="flex flex-col items-center leading-tight">
                            <span>{formatDateDisplay(rec.date)}</span>
                            <span className="text-[10px] font-semibold text-gray-500">
                              {formatDayDisplay(rec.date)}
                            </span>
                          </div>
                        </td>
                        <td className="p-3 border font-bold text-indigo-600">{rec.shiftCode || "--"}</td>
                        <td className="p-3 border font-semibold text-green-700">{rec.checkInTime || "--"}</td>
                        <td className="p-3 border text-gray-500 font-semibold italic">{rec.shiftStartTime || "--"}</td>
                        <td className="p-3 border text-gray-500 font-semibold italic">{rec.shiftEndTime || "--"}</td>
                        <td className="p-3 border font-semibold text-orange-700">{rec.checkOutTime || "--"}</td>
                        
                        {/* 7. Shift Work Duration (Rounded) */}
                        <td className="p-3 border font-bold text-blue-700">
                          {rec.workDuration || "--"}
                        </td>

                        {/* 8. Actual Work Duration (Raw Punch Time from DB) */}
                        <td className="p-3 border font-bold text-purple-700">
                          {rec.actualWorkDuration || "--"}
                        </td>
                        <td className="p-3 border">
                        {rec.isOT && rec.otHours > 0 ? (
                      <span className="bg-red-100 text-red-600 px-2 py-1 rounded-md font-bold">
                        {formatOTDisplay(rec.otHours)}
                      </span>
                          ) : (
                            <span className="text-gray-400">--</span>
                          )}
                        </td>
                        <td className="p-3 border">
                        <div className="flex flex-col items-center justify-center gap-1">
                          {(() => {
                            let displayText = statusText || "--";
                            if (statusText === "OFF") {
                              let prevStatus = null;
                              for (let i = index + 1; i < filteredRecords.length; i++) {
                                const s = String(filteredRecords[i]?.status || "").trim();
                                if (s !== "OFF") { prevStatus = s; break; }
                              }
                              if (prevStatus !== "Present") {
                                displayText = "Absent(OFF)";
                              }
                            }

                            const isAbsentState = statusText === "Absent" || displayText === "Absent(OFF)";

                            return (
                              <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                                statusText === 'Present' ? 'bg-green-100 text-green-700' :
                                isAbsentState ? 'bg-red-100 text-red-700' :
                                statusText === 'OFF' || statusText === 'OFF(EXCH)' ? 'bg-gray-100 text-gray-700' :
                                statusText.includes('SL') ? 'bg-orange-100 text-orange-700' :
                                statusText.includes('CL') ? 'bg-blue-100 text-blue-700' :
                                statusText === '' ? 'bg-white text-slate-400' :
                                'bg-yellow-100 text-yellow-700'
                              }`}>
                                {displayText}
                              </span>
                            );
                          })()}
                          
                          {/* Late Entry Indicator */}
                          {(rec.isLateEntry || (statusText === 'Present' && rec.isLate)) && (
                            <span className="text-[10px] text-red-500 font-bold uppercase animate-pulse">
                              Late Entry
                            </span>
                          )}
                        </div>
                      </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    {/* Changed colSpan to 9 to match new columns */}
                    <td colSpan="10" className="p-10 text-gray-400 italic text-center">No records found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeAttendance;

